import { useRef } from 'react';

import type { Editor, EditorEvents } from '@tiptap/core';

// Tiptap normalizes content at mount (b→strong, trailing node, list markers) before
// any user input; emitting that would falsely dirty the caller's draft
export function useMountNormalizationGuard(
  serialize: (editor: Editor) => string,
  onChange: (value: string) => void
) {
  const baselineRef = useRef<string | null>(null);
  const editedRef = useRef(false);

  const onCreate = ({ editor }: EditorEvents['create']) => {
    baselineRef.current ??= serialize(editor);
  };

  const onUpdate = ({ editor }: EditorEvents['update']) => {
    const value = serialize(editor);
    // The trailing-node init transaction can fire before `create`, so the first
    // value seen from either hook is the normalization baseline, not a user edit.
    // Only mount is guarded — once the user edits, typing back to the baseline is
    // itself an edit the caller must hear about
    const stillAtMount =
      !editedRef.current &&
      (baselineRef.current === null || value === baselineRef.current);
    if (stillAtMount) {
      baselineRef.current = value;
      return;
    }
    editedRef.current = true;
    onChange(value);
  };

  return { onCreate, onUpdate };
}
